import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LockKey, GoogleLogo } from "@phosphor-icons/react";
import Seo from "../components/Seo";

const AdminLogin = () => {
  const { admin, login, loginWithGoogleSession } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const processed = useRef(false);

  // Emergent Google Auth callback: URL fragment carries session_id after Google login.
  useEffect(() => {
    if (processed.current) return;
    const hash = location.hash || "";
    if (!hash.includes("session_id=")) return;
    processed.current = true;
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) return;
    setGoogleBusy(true);
    setErr("");
    // Immediately strip the fragment so a reload doesn't re-run the exchange
    window.history.replaceState({}, "", window.location.pathname);
    (async () => {
      try {
        const user = await loginWithGoogleSession(sid);
        nav(user.role === "door" ? "/checkin" : "/admin", { replace: true });
      } catch (ex) {
        setErr(ex?.response?.data?.detail || "Google sign-in failed");
        setGoogleBusy(false);
      }
    })();
  }, [location.hash, loginWithGoogleSession, nav]);

  if (admin) return <Navigate to={admin.role === "door" ? "/checkin" : "/admin"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const user = await login(email, pwd);
      nav(user.role === "door" ? "/checkin" : "/admin");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/admin/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="grain min-h-screen flex items-center cyber" data-testid="admin-login-page">
      <Seo title="Admin login" noindex />
      <div className="max-w-md w-full mx-auto px-5">
        <div className="hard-border bg-cyber-surface p-10">
          <div className="flex items-center gap-3 mb-6">
            <LockKey size={28} weight="duotone" className="text-cyber-yellow" />
            <div>
              <div className="term-label">/ admin · access_terminal</div>
              <h1 className="font-display font-black tracking-tighter text-3xl mt-1">Sign in.</h1>
            </div>
          </div>

          {googleBusy && (
            <div className="hard-border border-cyber-green text-cyber-green p-3 font-mono-tech text-xs mb-5" data-testid="admin-google-busy">
              / verifying Google session…
            </div>
          )}

          {/* Google Auth (whitelisted to existing admin emails) */}
          <button
            type="button"
            onClick={startGoogleLogin}
            disabled={busy || googleBusy}
            className="w-full flex items-center justify-center gap-3 hard-border bg-white text-cyber-black px-4 py-3 font-mono-tech text-xs uppercase tracking-[0.18em] font-bold hover:bg-white/90 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-google-login"
          >
            <GoogleLogo size={18} weight="bold" /> Sign in with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/40">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-white/70 block mb-2">Email</label>
              <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-3" required />
            </div>
            <div>
              <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-white/70 block mb-2">Password</label>
              <input data-testid="admin-password-input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="w-full px-4 py-3" required />
            </div>
            {err && <div className="hard-border border-cyber-red text-cyber-red p-3 font-mono-tech text-xs" data-testid="admin-login-error">/ ERROR :: {err}</div>}
            <button data-testid="admin-login-submit" type="submit" disabled={busy || googleBusy} className="btn-neon w-full justify-center">
              {busy ? "Connecting..." : "Authenticate"}
            </button>
          </form>
          <div className="mt-6 text-white/40 font-mono-tech text-xs leading-relaxed">
            / hint: Google sign-in only works for emails already invited as admins.
          </div>
        </div>
      </div>
    </main>
  );
};

export default AdminLogin;
